import { localized, msg } from '@lit/localize';
import { css, html, LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import '@spectrum-web-components/toast/sp-toast.js';
import {
  KV_TOAST_EVENT,
  type KvToastDetail,
  type KvToastVariant,
} from './toast-events';

const TOAST_TIMEOUT_MS = 6000;
const MAX_VISIBLE_TOASTS = 4;

type ToastItem = {
  message: string;
  variant: KvToastVariant;
};

type SpectrumToastVariant = 'positive' | 'negative' | 'info';

function normalizeVariant(value: unknown): KvToastVariant {
  return value === 'success' || value === 'error' || value === 'info'
    ? value
    : 'info';
}

function toSpectrumVariant(variant: KvToastVariant): SpectrumToastVariant {
  if (variant === 'success') return 'positive';
  if (variant === 'error') return 'negative';
  return 'info';
}

@customElement('kv-toast-center')
@localized()
export class KvToastCenter extends LitElement {
  static properties = {
    toasts: { state: true },
  };

  static styles = css`
    :host {
      bottom: 24px;
      box-sizing: border-box;
      display: block;
      pointer-events: none;
      position: fixed;
      right: 24px;
      z-index: 9000;
    }

    .toast-stack {
      align-items: end;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    sp-toast {
      max-width: calc(100vw - 32px);
      pointer-events: auto;
      width: 360px;
    }

    .toast-message {
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    @media (max-width: 768px) {
      :host {
        bottom: max(16px, env(safe-area-inset-bottom));
        left: 16px;
        right: 16px;
      }

      .toast-stack {
        align-items: stretch;
      }

      sp-toast {
        max-width: none;
        width: 100%;
      }
    }
  `;

  declare toasts: ToastItem[];

  constructor() {
    super();
    this.toasts = [];
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener(KV_TOAST_EVENT, this.onToast as EventListener);
  }

  disconnectedCallback(): void {
    window.removeEventListener(KV_TOAST_EVENT, this.onToast as EventListener);
    super.disconnectedCallback();
  }

  showToast(detail: KvToastDetail): void {
    const message =
      typeof detail?.message === 'string' ? detail.message.trim() : '';
    if (!message) {
      return;
    }
    const toast: ToastItem = {
      message,
      variant: normalizeVariant(detail?.variant),
    };
    this.toasts = [...this.toasts, toast].slice(-MAX_VISIBLE_TOASTS);
  }

  private onToast = (event: CustomEvent<KvToastDetail>) => {
    this.showToast(event.detail);
  };

  private dismissToast(toast: ToastItem): void {
    this.toasts = this.toasts.filter((item) => item !== toast);
  }

  render() {
    if (this.toasts.length === 0) {
      return nothing;
    }
    return html`
      <div
        class="toast-stack"
        role="region"
        aria-label=${msg('Notifications')}
      >
        ${this.toasts.map(
          (toast) => html`
            <sp-toast
              open
              .timeout=${TOAST_TIMEOUT_MS}
              variant=${toSpectrumVariant(toast.variant)}
              @close=${() => this.dismissToast(toast)}
            >
              <span class="toast-message">${toast.message}</span>
            </sp-toast>
          `
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-toast-center': KvToastCenter;
  }
}
